if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const formData = new FormData(this.form);
        const productAddons = this.getProductAddons();
        const quantity = Number(formData.get('quantity') || 1);
        const addonGroupId = productAddons?.shouldAddAddonProduct?.() ? this.createAddonGroupId() : null;
        const addonItem = this.buildAddonItem(productAddons, quantity, addonGroupId);

        const request = addonItem
          ? this.submitMainAndAddon(formData, addonItem, addonGroupId)
          : this.submitStandardForm(formData);

        request
          .then((response) => (typeof response.json === 'function' ? response.json() : response))
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!response.key && response.items?.length) response.key = response.items[0].key;
            if (!response.id && response.items?.length) response.id = response.items[0].id;

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      submitStandardForm(formData, renderSections = true) {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        if (this.cart && renderSections) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        config.body = formData;
        return fetch(`${routes.cart_add_url}`, config);
      }

      submitMainAndAddon(formData, addonItem, addonGroupId) {
        if (addonGroupId) {
          formData.append('properties[_addon_group]', addonGroupId);
          formData.append('properties[_addon_role]', 'main');
        }

        return this.submitStandardForm(formData, false).then((response) =>
          response.json().then((mainResponse) => {
            if (mainResponse.status) {
              return mainResponse;
            }

            return this.submitAddonOnly(addonItem).then((addonResponse) =>
              addonResponse.json().then((parsedAddonResponse) => {
                if (!parsedAddonResponse.key && mainResponse.key) parsedAddonResponse.key = mainResponse.key;
                if (!parsedAddonResponse.id && mainResponse.id) parsedAddonResponse.id = mainResponse.id;
                return parsedAddonResponse;
              })
            );
          })
        );
      }

      submitAddonOnly(addonItem) {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        const body = {
          items: [addonItem],
        };
        if (this.cart) {
          body.sections = this.cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          this.cart.setActiveElement(document.activeElement);
        }

        config.body = JSON.stringify(body);
        return fetch(`${routes.cart_add_url}`, config);
      }

      buildMainItem(formData, quantity, addonGroupId = null) {
        const item = {
          id: Number(formData.get('id')),
          quantity,
        };

        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) item.selling_plan = sellingPlan;

        const properties = {};
        formData.forEach((value, key) => {
          if (!key.startsWith('properties[') || value === '') return;

          const propertyName = key.replace(/^properties\[/, '').replace(/\]$/, '');
          properties[propertyName] = value;
        });

        this.mergeAddonProperties(properties);

        if (addonGroupId) {
          properties._addon_group = addonGroupId;
          properties._addon_role = 'main';
        }

        if (Object.keys(properties).length) item.properties = properties;

        return item;
      }

      buildAddonItem(productAddons, quantity, addonGroupId = null) {
        if (!productAddons) return null;

        const toggle = productAddons.querySelector('[data-fall-piku-toggle]');
        const variantId = Number(productAddons.dataset.fallPikuVariantId || 0);
        const isAvailable = productAddons.dataset.fallPikuAvailable === 'true';
        const mainProductTitle = productAddons.dataset.mainProductTitle || '';

        if (!toggle?.checked || !variantId || !isAvailable) return null;

        const addonProperties = {
          _addon_group: addonGroupId || this.createAddonGroupId(),
          _addon_role: 'fall_piku',
        };

        if (mainProductTitle) {
          addonProperties._addon_main_title = mainProductTitle;
        }

        return {
          id: variantId,
          quantity,
          properties: addonProperties,
        };
      }

      getProductAddons() {
        const sectionId = this.dataset.sectionId || this.closest('product-info')?.dataset.section;
        const formId = this.form?.id;
        const sectionSelector = sectionId ? `product-addons[data-section-id="${sectionId}"]` : null;
        const formSelector = formId ? `product-addons[data-product-form-id="${formId}"]` : null;

        return (
          (sectionSelector && this.closest('product-info')?.querySelector(sectionSelector)) ||
          (sectionSelector && document.querySelector(sectionSelector)) ||
          (formSelector && this.closest('product-info')?.querySelector(formSelector)) ||
          this.form.querySelector('product-addons') ||
          (formSelector && document.querySelector(formSelector)) ||
          null
        );
      }

      mergeAddonProperties(properties) {
        const productAddons = this.getProductAddons();

        if (!productAddons) return;

        productAddons.querySelectorAll('[data-blouse-property], [data-blouse-size-property], [data-addon-property]').forEach((input) => {
          if (input.disabled || !input.name || input.value === '') return;

          const propertyName = input.name.replace(/^properties\[/, '').replace(/\]$/, '');
          properties[propertyName] = input.value;
        });
      }

      createAddonGroupId() {
        return `addon-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      }
    }
  );
}
